import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../../data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Logo = ({ dark, onNavigate }) => (
  <a
    href="#top"
    data-testid="nav-logo"
    onClick={(e) => {
      e.preventDefault();
      if (onNavigate) onNavigate();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }}
    className="flex items-center gap-3 group"
  >
    <img
      src="/logo.png"
      alt="Home-Szig logó"
      className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
    />
    <span
      className={`font-heading text-xl font-extrabold uppercase tracking-tight transition-colors ${
        dark ? "text-white" : "text-brand-primary"
      }`}
    >
      <span className="text-brand-accent">Home</span>szig
    </span>
  </a>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const handleNav = (href) => {
    setOpen(false);
    setTimeout(() => scrollTo(href), open ? 300 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_rgba(10,37,64,0.06)]"
            : "bg-transparent"
        }`}
      >
        <nav
          className={`mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-12 transition-all duration-300 ${
            scrolled ? "py-3" : "py-5"
          }`}
        >
          <Logo dark={!scrolled} />

          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-link-${link.href.replace("#", "")}`}
                onClick={() => handleNav(link.href)}
                className={`relative text-sm font-medium transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-brand-accent after:transition-all hover:after:w-full ${
                  scrolled
                    ? "text-brand-primary/80 hover:text-brand-primary"
                    : "text-white/85 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-phone"
              className={`flex items-center gap-2 text-sm font-semibold transition-colors hover:text-brand-accent ${
                scrolled ? "text-brand-primary" : "text-white"
              }`}
            >
              <Phone size={16} />
              {CONTACT.phone}
            </a>
            <button
              data-testid="nav-cta"
              onClick={() => handleNav("#kapcsolat")}
              className="bg-brand-accent px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-brand-accentDark"
            >
              Ingyenes felmérés
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden grid h-10 w-10 place-items-center transition-colors ${
              scrolled ? "text-brand-primary" : "text-white"
            }`}
            onClick={() => setOpen(true)}
            aria-label="Menü megnyitása"
          >
            <Menu size={26} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-brand-primary text-white lg:hidden"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <Logo dark onNavigate={() => setOpen(false)} />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="grid h-10 w-10 place-items-center"
                aria-label="Menü bezárása"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col px-6 py-8">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.href}
                  data-testid={`mobile-nav-link-${link.href.replace("#", "")}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1 }}
                  onClick={() => handleNav(link.href)}
                  className="border-b border-white/10 py-4 text-left font-heading text-2xl font-bold"
                >
                  {link.label}
                </motion.button>
              ))}
              <a
                href={CONTACT.phoneHref}
                data-testid="mobile-phone"
                className="mt-8 flex items-center gap-3 text-lg font-semibold text-brand-accent"
              >
                <Phone size={20} /> {CONTACT.phone}
              </a>
              <button
                data-testid="mobile-cta"
                onClick={() => handleNav("#kapcsolat")}
                className="mt-6 bg-brand-accent px-6 py-4 font-semibold text-white"
              >
                Ingyenes felmérést kérek
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
